import axios from 'axios';
import ngAnnotate from '../ngAnnotate.macro';

angular.module('bb.admin').factory('AdminService', AdminService);

ngAnnotate(AdminService);

export type AdminService = ReturnType<typeof AdminService>;

function AdminService(
  $resource: ng.resource.IResourceService,
  $q: ng.IQService,
) {
  // Resources
  const Appointment = $resource('/appointments/:appointmentId', {
    appointmentId: '@appointmentId',
  });
  const AppointmentFlag = $resource('/appointments/:appointmentId/flag', {
    appointmentId: '@appointmentId',
  });
  const ResourceLoader = $resource('/org/appointments/:requestType', {
    requestType: '@requestType',
  });
  const ListingImages = $resource('/file/:listingId/images', {
    listingId: '@listingId',
  });
  const Listing = $resource('/listing/:listingId', {
    listingId: '@listingId',
  });
  const Duplicates = $resource('/listing/duplicates/:id', { id: '@id' });
  const OrgLocations = $resource('/org/locations');
  const AdminQueuePreferences = $resource('/user/current/admin_queue_prefs');
  const Uncombine = $resource('/uncombine/:combinedId', {
    combinedId: '@combinedId',
  });
  const IsCombinedListingLatest = $resource('/uncombine/:combinedId/latest', {
    combinedId: '@combinedId',
  });
  const SetPendingNoMoreShowings = $resource(
    '/listing/:listingId/appointments/config/pendingNoMoreShowings/:pendingNoMoreShowings',
    {
      listingId: '@listingId',
      pendingNoMoreShowings: '@pendingNoMoreShowings',
    },
  );
  const AnsweringOrg = $resource('/answering/org/:orgId', {
    orgId: '@orgId',
  });

  // Functions
  const getAppointment = (appointmentId: string) =>
    Appointment.get({ appointmentId }).$promise;

  const flagAppointment = (appointmentId: string, flagged: boolean) =>
    AppointmentFlag.save({ appointmentId, flagged }).$promise;

  const loadResource = (options: todo) => ResourceLoader.get(options).$promise;

  const dismissNoMoreShowings = (listingId: string) =>
    SetPendingNoMoreShowings.save({ listingId, pendingNoMoreShowings: true })
      .$promise;

  const getListing = (listingId: string) => Listing.get({ listingId }).$promise;

  const getListingImages = (listingId: string) =>
    ListingImages.query({ listingId }).$promise;

  const dismissDuplicates = (id: string) => Duplicates.delete({ id }).$promise;

  const getOrgLocations = () => OrgLocations.get().$promise;

  const getQueuePreferences = () => AdminQueuePreferences.get().$promise;

  const saveQueuePreferences = (adminQueuePreferences: todo) =>
    AdminQueuePreferences.save({ adminQueuePreferences }).$promise;

  const deleteQueuePreferences = () => AdminQueuePreferences.delete().$promise;

  const uncombineListing = (combinedId: string) =>
    Uncombine.save({ combinedId }).$promise;

  const isCombinedListingLatest = async (combinedId: string) =>
    IsCombinedListingLatest.get({ combinedId }).$promise;

  const getAnsweringOrg = (orgId: string) =>
    AnsweringOrg.get({ orgId }).$promise;
  // Using axios here to help with future migration
  const getAnsweringSettings = (location: string) =>
    $q((resolve, reject) => {
      axios
        .get(`/org/answeringsettings/${location}`)
        .then(({ data }) => resolve(data))
        .catch(reject);
    });

  /**
   * @deprecated, remove when legacy answering service page admin_answering.component.js is converted to react.
   */
  const getPortalAnsweringConfig = () =>
    $q((resolve, reject) => {
      axios
        .get('/answering_subscription/config')
        .then(({ data }) => resolve(data))
        .catch(reject);
    });

  const saveAnsweringSettings = (location: string, answeringSettings: todo) =>
    $q((resolve, reject) => {
      axios
        .post(`/org/answeringsettings/${location}`, { ...answeringSettings })
        .then(({ data }) => resolve(data))
        .catch(reject);
    });

  const toggleIgnoreAnsweringSettings = () =>
    $q((resolve, reject) => {
      axios
        .post('/org/answeringsettings/ignore')
        .then(({ data }) => resolve(data))
        .catch(reject);
    });

  return {
    getAppointment,
    flagAppointment,
    loadResource,
    dismissNoMoreShowings,
    getListingImages,
    getListing,
    dismissDuplicates,
    getOrgLocations,
    getQueuePreferences,
    saveQueuePreferences,
    deleteQueuePreferences,
    uncombineListing,
    isCombinedListingLatest,
    getAnsweringOrg,
    getAnsweringSettings,
    getPortalAnsweringConfig,
    saveAnsweringSettings,
    toggleIgnoreAnsweringSettings,
  };
}
